import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, ChevronUp, GripVertical, Edit2, Trash2, Archive, RotateCcw, CheckSquare, Clock } from 'lucide-react';
import { format } from 'date-fns';

export default function ActivityHeaderCell({
  activity,
  provided,
  snapshot,
  groupEmail,
  groupByMode,
  expandedOfficers,
  setExpandedOfficers,
  eisenhowerLabels,
  officers,
  dailyTasks,
  user,
  handleEdit,
  handleArchiveClick,
  handleDeleteClick,
  unarchiveActivityMutation,
}) {
  const activityTasks = dailyTasks.filter(t => t.activity_id === activity.id);
  const completedCount = activityTasks.filter(t => t.completed).length;

  return (
    <th
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={`p-4 text-left align-top border-r border-slate-800 last:border-r-0 min-w-[350px] max-w-[450px] ${
        snapshot.isDragging ? 'bg-slate-800/50 text-slate-200' :
        activity.status === 'archived' ? 'bg-slate-600/20 text-slate-400' :
        activity.status_flag === 'green' ? 'bg-emerald-500 text-white' :
        activity.status_flag === 'red' ? 'bg-red-500 text-white' :
        'bg-amber-500 text-white'
      }`}
    >
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-200 flex-shrink-0">
              <GripVertical className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold mb-1 ${activity.status !== 'archived' ? 'text-white/90' : 'text-indigo-400'}`}>
                {activity.activity_code || 'N/A'}
              </div>
              <div className="text-xs opacity-70 mb-1">
                {activity.reactivated_at
                  ? `Reactivated: ${format(new Date(activity.reactivated_at), 'MMM d, yyyy')}`
                  : `Created: ${format(new Date(activity.created_date), 'MMM d, yyyy')}`}
              </div>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-lg font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-white via-slate-100 to-slate-400 drop-shadow-md tracking-tight leading-none py-1 whitespace-nowrap overflow-hidden text-ellipsis">
                      {activity.title}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs bg-slate-800 text-slate-100 border-slate-700 text-sm font-medium whitespace-normal">
                    {activity.title}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7"
              onClick={() => setExpandedOfficers((prev) => ({ ...prev, [groupEmail]: !prev[groupEmail] }))}>
              {expandedOfficers[groupEmail] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            {(activity.accountable_email === user?.email || activity.responsible_email === user?.email) && (
              <>
                {activity.status === 'archived' ? (
                  <Button size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => unarchiveActivityMutation.mutate(activity.id)} title="Unarchive">
                    <RotateCcw className="w-3 h-3" />
                  </Button>
                ) : (
                  <Button size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => handleArchiveClick(activity)} title="Archive">
                    <Archive className="w-3 h-3" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEdit(activity)}>
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleDeleteClick(activity)} title="Delete">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          <Badge className={`${eisenhowerLabels[activity.eisenhower_quadrant]?.color || 'bg-slate-500/20 text-slate-400'} ${
            activity.status !== 'archived' ? 'bg-white/20 text-white border-white/30' : ''
          }`}>
            {eisenhowerLabels[activity.eisenhower_quadrant]?.label || 'N/A'}
          </Badge>
          <Badge className={activity.status === 'archived' ? 'bg-slate-600/20 text-slate-500 border-none' : 'bg-white/20 text-white border-white/30'}>
            {activity.status}
          </Badge>
          <Badge className="bg-white/20 text-white border-white/30">
            KR: {activity.key_results_progress || 0}%
          </Badge>
          {!expandedOfficers[groupEmail] && (() => {
            const showAccountable = groupByMode === 'responsible';
            const showResponsible = groupByMode === 'accountable';
            if (!showAccountable && !showResponsible) return null;
            const email = showAccountable ? activity.accountable_email : activity.responsible_email;
            const label = showAccountable ? 'A:' : 'R:';
            if (!email) {
              if (showResponsible) return <Badge className="bg-slate-800/50 text-slate-500 border-slate-700 border">R: TBD</Badge>;
              return null;
            }
            const officer = officers.find(o => o.user_email === email);
            const text = officer
              ? `${label} ${officer.callsign} (${officer.rank_title} L${officer.clearance_level})`
              : `${label} ${email}`;
            return <Badge className="bg-slate-800/50 text-slate-300 border-slate-700 border">{text}</Badge>;
          })()}
          {activityTasks.length > 0 && (
            <Badge className="bg-slate-700/50 text-slate-300 border-slate-600 border flex items-center gap-1">
              <CheckSquare className="w-3 h-3" />
              {completedCount}/{activityTasks.length} Tasks
            </Badge>
          )}
        </div>

        {!expandedOfficers[groupEmail] && (
          <div className="mt-3 space-y-2 text-sm">
            {(activity.key_results || []).length > 0 && (
              <div className="space-y-1">
                {activity.key_results.slice(0, 2).map((kr, idx) => {
                  const current = parseFloat(kr.current_value) || 0;
                  const target = parseFloat(kr.target) || 1;
                  const progress = Math.min(current / target * 100, 100);
                  return (
                    <div key={idx}>
                      <div className="flex items-center justify-between text-xs mb-1 opacity-90">
                        <span>{kr.description}</span>
                        <span>{kr.current_value}/{kr.target} {kr.unit}</span>
                      </div>
                      <div className={`h-1.5 rounded-full overflow-hidden ${activity.status !== 'archived' ? 'bg-white/20' : 'bg-slate-800'}`}>
                        <div
                          className={`h-full ${activity.status !== 'archived' ? 'bg-white/80' : 'bg-gradient-to-r from-indigo-500 to-cyan-500'}`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                {activity.key_results.length > 2 && (
                  <div className="text-xs opacity-70">+{activity.key_results.length - 2} more...</div>
                )}
              </div>
            )}
            {activity.due_date && (
              <div className="flex items-center gap-1.5 text-xs opacity-90">
                <Clock className="w-3 h-3" />
                Due: {format(new Date(activity.due_date), 'MMM d, yyyy')}
              </div>
            )}
          </div>
        )}
      </div>
    </th>
  );
}